import { useContext, useEffect } from "react";

import {
    Role,
    RoleApiResponse,
    RoleWithPermissions
} from "components/TableDetails/UserDetails/Permissions/types";
import { getAllPermissions } from "context/auth";
import LanguageContext from "context/language/languageContext";
import ParamsContext from "context/params/paramsContext";
import TableContext from "context/table/tableContext";
import {
    API_IS_BEING_CANCELLED,
    COLUMN_IDENTIFIERS,
    COLUMN_NAMES,
    COLUMN_RESOURCES,
    ENDPOINTS,
    FILTER_NAMES,
    getStringifiedParams,
    InitialColumn,
    NOT_FILTER_NAMES,
    PER_PAGE,
    QueryParamName,
    QueryParams,
    TABLE_NAMES,
    useApi,
    useIsMounted,
    useParamsFromUrlAndBrowser
} from "shared";

export const useTableData = () => {
    const {
        queryParams,
        stringParams,
        reloadItems,
        isRecurringReload,
        removeRecurringReload
    } = useContext(ParamsContext);

    const { language } = useContext(LanguageContext);

    const {
        hasNoPermissionToRead,
        customData,
        isInnerTable,
        tableName,
        customResource,
        sortFromNewest,
        innerTableQueryParams,
        innerTableStringParams,
        innerTableInitialParamNames,
        isTableLoading,
        setTableData,
        setTableLoading,
        getInnerTableData
    } = useContext(TableContext);

    const isMounted = useIsMounted();
    const { cancelSource, isCanceled, queryData, handleResponse } = useApi();
    const { stringifiedCurrentParams } = useParamsFromUrlAndBrowser();

    const reloadItem = reloadItems[tableName];
    const isPermissions = tableName === TABLE_NAMES.Permissions;

    const initialRowsPerPage =
        queryParams[NOT_FILTER_NAMES.PerPage] || PER_PAGE.SmallCount;

    const getDataOfAllRows = async () => {
        if (!isRecurringReload && !isTableLoading) {
            setTableLoading(true);
            getInnerTableData?.({ isInnerTableLoading: true });
        }

        try {
            const parameters = isInnerTable
                ? innerTableStringParams
                : stringParams;

            const hasSort =
                !isInnerTable &&
                !Object.keys(queryParams).includes(NOT_FILTER_NAMES.Sort) &&
                sortFromNewest;

            const response = await queryData(
                customResource || tableName,
                parameters,
                hasSort
            );

            if (isMounted) {
                const {
                    data: { current_page, data, per_page, total }
                } = response;

                const payload = {
                    rows: data,
                    currentPage: current_page,
                    rowsPerPage: per_page,
                    totalRows: total
                };

                setTableData(payload);

                getInnerTableData?.({
                    isInnerTableLoading: false,
                    ...payload
                });
            }
        } catch (error) {
            if (isMounted && !isCanceled(error)) {
                handleResponse(error);

                const payload = {
                    rows: [],
                    currentPage: 1,
                    rowsPerPage: initialRowsPerPage,
                    totalRows: 0
                };

                setTableData(payload);

                getInnerTableData?.({
                    isInnerTableLoading: false,
                    ...payload
                });
            }
        }

        removeRecurringReload();
    };

    // To avoid duplicate requests when going through pages when filters are applied
    const areSameParams = stringParams === stringifiedCurrentParams;

    // Get main table data
    useEffect(() => {
        !hasNoPermissionToRead &&
            !customData &&
            !isInnerTable &&
            areSameParams &&
            getDataOfAllRows();

        return () => {
            cancelSource.cancel(API_IS_BEING_CANCELLED);
        };

        // eslint-disable-next-line
    }, [stringParams, areSameParams, reloadItem]);

    // Get inner table data
    useEffect(() => {
        !hasNoPermissionToRead &&
            !isPermissions &&
            !customData &&
            isInnerTable &&
            getDataOfAllRows();

        return () => {
            cancelSource.cancel(API_IS_BEING_CANCELLED);
        };

        // eslint-disable-next-line
    }, [innerTableStringParams, customResource, reloadItem]);

    // Get permissions data
    const idFilter = innerTableQueryParams[FILTER_NAMES.Id];

    useEffect(() => {
        const getInnerTableInitialParams = (
            innerTableAllQueryParams: QueryParams,
            innerTableInitialParamNames: QueryParamName[]
        ) => {
            const copy = { ...innerTableAllQueryParams };

            for (const property in copy) {
                const isInitialParam = innerTableInitialParamNames.includes(
                    property as QueryParamName
                );

                if (!isInitialParam) {
                    delete copy[property as QueryParamName];
                }
            }

            return copy;
        };

        const createRowsData = (
            rolesWithPermissions: RoleWithPermissions[]
        ) => {
            const allPermissions = getAllPermissions();

            return allPermissions.map(permission => ({
                [COLUMN_RESOURCES.Id]: permission,
                [COLUMN_RESOURCES.Name]: permission,
                [COLUMN_RESOURCES.RoleStatuses]: {
                    ...rolesWithPermissions.reduce((acc, role) => {
                        return {
                            ...acc,
                            [role.id]: role.permissions.includes(permission)
                        };
                    }, {})
                }
            }));
        };

        const getPermissionsData = async () => {
            if (idFilter) {
                const innerTableInitialParams = getInnerTableInitialParams(
                    innerTableQueryParams,
                    innerTableInitialParamNames
                );

                const initialStringifiedParams = getStringifiedParams(
                    innerTableInitialParams
                );

                try {
                    const { data } = await queryData(
                        ENDPOINTS.Roles,
                        initialStringifiedParams
                    );

                    const roles = data.data;

                    const rolesWithPermissions: RoleWithPermissions[] =
                        roles.map((role: RoleApiResponse) => ({
                            id: role.id,
                            permissions: role.permissions
                        }));

                    const createColumns = (): InitialColumn[] => {
                        return [
                            {
                                name: COLUMN_NAMES.Name,
                                resource: COLUMN_RESOURCES.Name,
                                isDefaultActive: true,
                                isActive: true,
                                isSticky: true,
                                isDisabled: true,
                                isValueTranslatable: true
                            },
                            ...roles.map((role: Role) => ({
                                name: role.name as COLUMN_NAMES,
                                resource: [
                                    COLUMN_RESOURCES.RoleStatuses,
                                    role.id
                                ],
                                columnIdentifier:
                                    COLUMN_IDENTIFIERS.RoleStatuses,
                                isDefaultActive: true,
                                isActive: true,
                                isNameNotTranslatable: true
                            }))
                        ];
                    };

                    setTableData({
                        columns: createColumns(),
                        rows: createRowsData(rolesWithPermissions)
                    });
                } catch (error) {
                    setTableData({ columns: [], rows: [] });
                    handleResponse(error);
                }
            } else {
                setTableData({ columns: [], rows: [] });
            }
        };

        !hasNoPermissionToRead && isPermissions && getPermissionsData();
        // eslint-disable-next-line
    }, [idFilter, language]);
};
